import { ReactElement, useEffect, useRef, useState } from 'react';
import { Component as PlusIcon } from 'icons/icon-ui--plus.svg';
import { Component as SaleTagIcon } from 'icons/icon-ui--sale-tag.svg';
import { useFlags, useFlagsmith } from '@flagsmith/flagsmith/react';
import { useNavigateWithUTMs } from '../../hooks/useNavigateWithUTMs';
import { Button } from '../shared/button/Button';
import { QuantitySelector } from '../shared/quantity-selector/QuantitySelector';
import { Badge } from '../shared/badge/Badge';
import { Favorite } from '../favorite/Favorite';
import styles from './ProductTile.module.scss';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { MenuCartItem } from '../../models/menu.types';
import { gtmTrackAddToCart } from '../../services/gtm/gtm';
import { parsePricePerRecipe } from '../../utils/prices';
import { ProductRating } from '../product-display-page/ReviewsContent/ProductRating';

type Props = {
  title: string;
  subTitle?: string;
  imageUrl: string;
  totalPrice: string;
  quantity: number;
  isInStock: boolean;
  isActive: boolean
  isLoggedIn?: boolean;
  badgeTags: string[];
  cookTime: string;
  pricePerServing?: string; // for MK and RTE
  salePricePerServing?: string;
  servingSize?: number; // for MK and RTE
  unitSize?: string; // for Grocery
  brandName?: string; // for Grocery
  onAddToCart: () => void;
  isSaleTagEnabled?: boolean;
  salePrice?: string;
  path: string;
  onUpdateCartQuantity: (quantity: number) => void;
  productType: string;
  skuId: number
  isLtoBox: boolean
  ltoServings: string
  description: string
  handleSoldOutLtoClick: () => void;
  soldOut: boolean;
  isFavorite: boolean;
  lazyLoadImage?: boolean;
  'data-heap-merch-group-title'?: string;
  productId: number;
};

export function ProductTile({
  title,
  subTitle,
  imageUrl,
  totalPrice,
  quantity,
  isInStock,
  isActive = true, // is the user's subscription active
  isLoggedIn = true,
  badgeTags,
  cookTime,
  pricePerServing,
  salePricePerServing,
  servingSize,
  unitSize,
  isSaleTagEnabled = false,
  salePrice,
  brandName,
  path,
  onAddToCart,
  onUpdateCartQuantity,
  productType,
  skuId,
  isLtoBox,
  ltoServings,
  description,
  handleSoldOutLtoClick,
  soldOut,
  isFavorite,
  lazyLoadImage = false,
  'data-heap-merch-group-title': merchGroupTitle,
  productId,
}: Props): ReactElement {
  const productTileSkuHeapId = `product_tile_sku_${skuId}`;
  const isOnSale = !!(isSaleTagEnabled && salePrice);
  const isGrocery = productType === 'Grocery';
  const showServingInfo = !!(pricePerServing && servingSize && !isLtoBox);
  const priceDisplay = isOnSale ? (
    <div className={styles.price_row}>
      <div className={styles.sale_price}>{salePrice}</div>
      <div className={styles.pre_sale_price}>{totalPrice}</div>
    </div>
  ) : (
    <span className={styles.bold_price}>{totalPrice}</span>
  );
  const { canIncreaseQuantity } = useMenuWithCart();
  const isAtCapacity = !canIncreaseQuantity({
    skuId,
    isInStock,
    soldOut,
    quantity,
  } as unknown as MenuCartItem);
  const cartActionButton = quantity > 0 ? (
    <QuantitySelector
      quantity={quantity}
      onAddQuantity={() => {
        if (!isLoggedIn) {
          gtmTrackAddToCart({
            price: parsePricePerRecipe(pricePerServing || totalPrice),
            currency: 'USD',
            ecomPagetype: 'home',
            item: {
              itemName: title,
              itemId: String(skuId),
              price: parsePricePerRecipe(pricePerServing || totalPrice),
              quantity: 1,
            },
          });
        }
        onUpdateCartQuantity(quantity + 1);
      }}
      onSubtractQuantity={() => onUpdateCartQuantity(quantity - 1)}
      isPlusDisabled={isAtCapacity}
    />
  ) : (
    <Button
      text="Add"
      icon={PlusIcon}
      onClick={() => {
        if (!isLoggedIn) {
          gtmTrackAddToCart({
            price: parsePricePerRecipe(pricePerServing || totalPrice),
            currency: 'USD',
            ecomPagetype: 'home',
            item: {
              itemName: title,
              itemId: String(skuId),
              price: parsePricePerRecipe(pricePerServing || totalPrice),
              quantity: 1,
            },
          });
        }
        onAddToCart();
      }}
      isEnabled={!isAtCapacity}
      className={styles.button_styling}
      heapId="add_button"
    />
  );
  const flags = useFlags(['display-customer-reviews-product-tile']);
  const customerReviewFlag = flags['display-customer-reviews-product-tile'];
  const showCustomerReviewsProductTile = customerReviewFlag.enabled && customerReviewFlag.value === 'show';
  const { navigate } = useNavigateWithUTMs();
  const handleClick = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    navigate(path, { state: { skuId } });
  };

  // Used for badge sizing. On small screens (<= md), this sizes the badges xs instead of sm.
  const [isScreenSmall, setIsScreenSmall] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 628px)');
    const handler = () => setIsScreenSmall(media.matches);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  const tileRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    if (!showCustomerReviewsProductTile) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    if (tileRef.current) observer.observe(tileRef.current);
    return () => observer.disconnect();
  }, [showCustomerReviewsProductTile]);

  const flagsmith = useFlagsmith();
  const isHardCodedRegisteredTrademarkDisplayEnabled = flagsmith.hasFeature('hard_coded_registered_trademark_display');
  return (
    <div
      ref={tileRef}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleClick();
        }
      }}
      className={styles.product_tile}
      onClick={handleClick}
      data-heap-id={productTileSkuHeapId}
      data-heap-merch-group-title={merchGroupTitle}
    >
      {isOnSale && (
        <div className={styles.sale_tag}>
          <SaleTagIcon />
          SALE!
        </div>
      )}
      {/* Favorite Icon Overlay if user is logged in */}
      {isActive && isLoggedIn && (
        <Favorite
          isFavorite={isFavorite}
          skuId={skuId}
        />
      )}
      <img
        className={styles.product_image}
        src={imageUrl}
        loading={lazyLoadImage ? 'lazy' : 'eager'}
        alt="temp-img"
      />
      <div className={styles.bottom_style}>
        <div className={styles.title_subtitle_container}>
          <div
            className={styles.title}
            title={title}
            style={{
              // -webkit-box-orient needed to wrap title to two lines then clamp with ellipsis.
              // eslint-disable-next-line max-len
              // This sometimes was not showing in browser through css file, so instead uses div style
              WebkitBoxOrient: 'vertical',
            }}
          >
            {title}
          </div>
          <div className={styles.sub_title}>{isLtoBox ? description : subTitle}</div>
        </div>

        {isGrocery && (
          <>
            <div className={styles.sub_title}>
              {brandName && (
                <div className={styles.brand_name}>
                  {brandName}
                  {isHardCodedRegisteredTrademarkDisplayEnabled && '®'}
                </div>
              )}
            </div>
            {unitSize && (
              <div className={styles.serving_description_text} style={{ paddingTop: '8px' }}>
                {priceDisplay}
                {' '}
                /
                {' '}
                {unitSize}
              </div>
            )}
          </>
        )}

        {!isGrocery && (
          <div className={styles.text}>
            {priceDisplay}
            {pricePerServing && ltoServings && isLtoBox && (
              <span className={styles.serving_description_text}>
                {` • ${pricePerServing} / each • Feeds ${ltoServings}`}
              </span>
            )}
            {showServingInfo && (
              <>
                <span className={styles.serving_description_text}>
                  {' • '}
                  <span className={isSaleTagEnabled ? styles.sale_text : ''}>
                    {`${
                      isSaleTagEnabled && salePricePerServing
                        ? salePricePerServing
                        : pricePerServing
                    } / serving`}
                  </span>
                </span>
                <span
                  className={styles.serving_description_text}
                >
                  {` • Feeds ${servingSize} •`}
                </span>
              </>
            )}
            {(productType === 'Meal Kit' || productType === 'Ready to Eat') && (
              <span className={styles.serving_description_text}>
                {` ${cookTime} `}
                {productType === 'Ready to Eat' ? 'RTE' : productType}
              </span>
            )}
          </div>
        )}

        {showCustomerReviewsProductTile && isInView
          && <ProductRating productId={productId} interactive={false} size="sm" />}

        {/* Tags and CTA section */}
        <div
          className={styles.product_tag_quantity_selector_container}
        >
          <div className={styles.product_tag_buttons_container}>
            {badgeTags.map((tag) => (
              <Badge
                key={tag}
                bgColor="#F4EFFF"
                color="#222"
                size={isScreenSmall ? 'xs' : 'sm'}
                text={tag}
              />
            ))}
          </div>

          {isActive && (
            <>
              {isInStock || quantity > 0 ? (
                cartActionButton
              ) : (
                <>
                  {isLtoBox ? (
                    <Button
                      text="Explore"
                      onClick={handleSoldOutLtoClick}
                    />
                  ) : (
                    <Button
                      text="Sold Out"
                      onClick={() => console.log('Sold Out')}
                      isEnabled={false}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
